import DsNumberCard from "./overview/dsNumberCard";
import { Box, Grid, Typography, Skeleton, CardHeader, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { UseFetchAgentSales, UseFetchDownlineSales, UseFetchGroupSales } from "../../hooks/useFetchSales";
import DsTable from "./overview/dsTable";
import { useEffect, useMemo } from "react";
import { handleFormatPriceString } from "../../utils/priceUtils";
import { UseFetchDownlineAgents, UseFetchGroupAgents } from "../../hooks/useFetchAgent";
import UseFetchAgentCommission from "../../hooks/useFetchCommission";
import { convertDateTimeToDateString } from "../../utils/datetimeUtils";
import { useUser } from "../../context/userContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

export default function DashboardBK() {
    const { user } = useUser();
    const navigate = useNavigate();
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];

    const isAgent = userRoles.some(r => ["In-house Agent", "Sales Agent"].includes(r));
    const isLeader = userRoles.includes("Leader");
    const isAdmin = userRoles.some(r => ["Admin", "Super Admin"].includes(r));

    // Fetch hooks - conditionally call based on roles

    const { data: salesArray, loading: salesLoading } = UseFetchAgentSales(isAgent);

    const { data: groupAgentArray, loading: groupAgentLoading } = UseFetchGroupAgents(isLeader);
    const { data: groupSalesArray, loading: groupSalesLoading } = UseFetchGroupSales(isLeader);

    const { data: downlineAgentArray, loading: downlineAgentLoading } = UseFetchDownlineAgents(isAgent);
    const { data: downlineSalesArray, loading: downlineSalesLoading } = UseFetchDownlineSales(downlineAgentArray, isAgent);

    const { data: agentCommissionArray, loading: agentCommissionLoading } = UseFetchAgentCommission(isAgent);

    const isLoading = (isAgent && (salesLoading || downlineAgentLoading || downlineSalesLoading || agentCommissionLoading)) ||
        (isLeader && (groupAgentLoading || groupSalesLoading));

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    // --- Aggregations ---

    // Own Sales (Agent)
    const totalFinalPrice = useMemo(() => {
        const arr = Array.isArray(salesArray) ? salesArray : (salesArray?.data ?? []);
        return arr.reduce((sum, item) => {
            const salesCreatedOn = item?.createdOn;

            if (!salesCreatedOn) {
                return sum;
            }

            const d = new Date(String(salesCreatedOn).trim());
            if (Number.isNaN(d.getTime()) || d.getFullYear() !== currentYear) {
                return sum;
            }

            return sum + Number(item?.finalPrice ?? 0);
        }, 0);
    }, [salesArray, currentYear]);

    const totalSalesThisMonth = useMemo(() => {
        let thisMonthTotalSales = 0;
        const arr = Array.isArray(salesArray) ? salesArray : (salesArray?.data ?? []);

        for (const sale of arr) {
            const date = new Date(sale.createdOn);
            const salesYear = date.getFullYear();
            const salesMonth = date.getMonth() + 1;

            // Check if the sale is in the current month and year
            if (salesYear === currentYear && salesMonth === currentMonth) {
                thisMonthTotalSales += Number(sale.finalPrice ?? 0);
            }
        }

        return thisMonthTotalSales;
    }, [salesArray, currentYear, currentMonth]);

    // Group Stats (Leader)
    const totalGroupAgents = groupAgentArray?.length ?? 0;
    const totalGroupSales = useMemo(() => {
        const arr = Array.isArray(groupSalesArray) ? groupSalesArray : (groupSalesArray?.data ?? []);
        return arr.reduce((sum, item) => sum + Number(item?.finalPrice ?? 0), 0);
    }, [groupSalesArray]);

    // Downline Stats (Agent)
    const totalAgentDownline = downlineAgentArray?.length ?? 0;
    const totalAgentDownlineSales = useMemo(() => {
        const arr = Array.isArray(downlineSalesArray) ? downlineSalesArray : (downlineSalesArray?.data ?? []);
        return arr.reduce((sum, item) => {
            const salesCreatedOn = item?.createdOn;

            if (!salesCreatedOn) {
                return sum;
            }

            const d = new Date(String(salesCreatedOn).trim());
            if (Number.isNaN(d.getTime()) || d.getFullYear() !== currentYear) {
                return sum;
            }

            return sum + Number(item?.finalPrice ?? 0);
        }, 0);
    }, [downlineSalesArray, currentYear]);

    // Commissions (Agent)
    const calculateCommission = (type, status = null) => {
        const arr = Array.isArray(agentCommissionArray) ? agentCommissionArray : (agentCommissionArray?.data ?? []);

        return arr.filter(item =>
            item?.commissionProgressDto?.commPayType === type &&
            (!status || item?.commissionProgressDto?.paymentStatus === status)
        ).reduce((sum, item) => {
            const salesCreatedOn = item?.salesCreatedOn;
            if (!salesCreatedOn) {
                return sum;
            }

            const d = new Date(String(salesCreatedOn).trim());
            if (Number.isNaN(d.getTime())) {
                return sum;
            }

            const itemYear = d.getFullYear();
            const itemMonth = d.getMonth() + 1;
            const amount = Number(item?.commissionProgressDto?.commPay ?? 0);

            if (itemYear === currentYear) {
                sum.totalCommThisYear += amount;
                if (itemMonth === currentMonth) {
                    sum.totalCommThisMonth += amount;
                }
            }

            return sum;
        }, { totalCommThisYear: 0, totalCommThisMonth: 0 });
    };

    const totalAgentPsc = useMemo(() => calculateCommission("PSC"), [agentCommissionArray, currentYear]);
    const totalAgentDoc = useMemo(() => calculateCommission("DOC"), [agentCommissionArray, currentYear]);
    const totalAgentToc = useMemo(() => calculateCommission("TOC"), [agentCommissionArray, currentYear]);

    const totalClaimedAgentPsc = useMemo(() => calculateCommission("PSC", "Complete"), [agentCommissionArray, currentYear]);
    const totalClaimedAgentDoc = useMemo(() => calculateCommission("DOC", "Complete"), [agentCommissionArray, currentYear]);
    const totalClaimedAgentToc = useMemo(() => calculateCommission("TOC", "Complete"), [agentCommissionArray, currentYear]);

    const totalAgentPscThisMonth = totalAgentPsc.totalCommThisMonth;
    const totalAgentDocThisMonth = totalAgentDoc.totalCommThisMonth;
    const totalAgentTocThisMonth = totalAgentToc.totalCommThisMonth;

    const totalAgentCommissionThisYear = totalAgentPsc.totalCommThisYear + totalAgentDoc.totalCommThisYear + totalAgentToc.totalCommThisYear;
    const totalClaimedAgentCommissionThisYear = totalClaimedAgentPsc.totalCommThisYear + totalClaimedAgentDoc.totalCommThisYear + totalClaimedAgentToc.totalCommThisYear;
    const totalPendingAgentCommissionThisYear = totalAgentCommissionThisYear - totalClaimedAgentCommissionThisYear;

    // Latest Sales
    const latestSalesColumns = [
        { id: 'customer', label: 'Customer Name' },
        { id: 'product', label: 'Product Name' },
        { id: 'depositOn', label: 'Deposit On' },
        { id: 'finalPrice', label: 'Final Price' },
        { id: 'status', label: 'Status' },
    ];

    const latestSalesData = useMemo(() => {
        const arr = Array.isArray(salesArray) ? salesArray : (salesArray?.data ?? []);
        return arr.slice(0, 20).map(s => ({
            id: s.salesId,
            customer: s.customerDetails?.customerName ?? "N/A",
            product: s.productDetails?.productName ?? "N/A",
            depositOn: convertDateTimeToDateString(s.depositOn) ?? "N/A",
            finalPrice: "RM " + (Number(String(s.finalPrice ?? s.amount ?? 0).replace(/[^0-9.-]+/g, "")) || 0),
            status: s.status ?? "N/A",
        }));
    }, [salesArray]);

    // Top Sales Agent
    const topSalesmanColumns = [
        { id: 'rank', label: 'Rank' },
        { id: 'agent', label: 'Agent Name' },
    ];

    const topSalesmanData = useMemo(() => {
        const arr = Array.isArray(groupSalesArray) ? groupSalesArray : (groupSalesArray?.data ?? []);
        const sorted = [...arr].sort((a, b) => {
            const salesA = Number(a?.finalPrice ?? 0);
            const salesB = Number(b?.finalPrice ?? 0);
            return salesB - salesA;
        });
        return sorted.slice(0, 10).map((item, index) => ({
            rank: index + 1,
            agent: item?.agentName,
            count: 1,
            formattedSales: handleFormatPriceString(Number(item?.finalPrice ?? 0)),
        }));
    }, [groupSalesArray]);

    //Top Sales Team
    const topSalesTeamColumns = [
        { id: 'rank', label: 'Rank' },
        { id: 'team', label: 'Team Name' },
        { id: 'salesRevenue', label: 'Total Sales' },
    ];

    const topSalesTeamData = useMemo(() => {
        const arr = Array.isArray(groupSalesArray) ? groupSalesArray : (groupSalesArray?.data ?? []);
        const sorted = [...arr].sort((a, b) => {
            const salesA = Number(a?.finalPrice ?? 0);
            const salesB = Number(b?.finalPrice ?? 0);
            return salesB - salesA;
        });
        return sorted.slice(0, 10).map((item, index) => ({
            rank: index + 1,
            agent: item?.agentName,
            count: 1,
            formattedSales: handleFormatPriceString(Number(item?.finalPrice ?? 0)),
        }));
    }, [groupSalesArray]);

    // --- Widget Configuration ---
    const items = [
        // Agent Widgets (Own Sales)
        {
            id: "PersonalSalesRevenue",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "Personal Sales Revenue",
                // description: "Since last year",
                // diff: totalFinalPrice,
                // trend: "up",
                value: handleFormatPriceString(totalFinalPrice)
            }
        },
        {
            id: "MonthlyPersonalSalesRevenue",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "Monthly Personal Sales Revenue",
                value: handleFormatPriceString(totalSalesThisMonth)
            }
        },
        // Agent Widgets (All Type Commissions)
        {
            id: "TotalClaimedCommissions",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "[Claimed] PSC + DOC + TOC",
                value: handleFormatPriceString(totalClaimedAgentCommissionThisYear)
            }
        },
        {
            id: "TotalPendingCommissions",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "[Pending] PSC + DOC + TOC",
                value: handleFormatPriceString(totalPendingAgentCommissionThisYear)
            }
        },
        // Leader Widgets (Group)
        {
            id: "TotalGroupAgents",
            roles: ["Leader"],
            Component: DsNumberCard,
            props:
            {
                title: "Group Agents",
                value: totalGroupAgents
            }
        },
        {
            id: "TotalGroupSales",
            roles: ["Leader"],
            Component: DsNumberCard,
            props:
            {
                title: "Total Group Sales",
                value: handleFormatPriceString(totalGroupSales)
            }
        },
        // Agent Widgets (Downlines)
        // {
        //     id: "TotalDownlineAgents",
        //     roles: ["Agent"],
        //     Component: DsNumberCard,
        //     props:
        //     {
        //         title: "Downline Agents",
        //         value: totalAgentDownline
        //     }
        // },
        {
            id: "TotalDownlineSales",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "Total Downline Sales",
                value: handleFormatPriceString(totalAgentDownlineSales)
            }
        },
        {
            id: "TotalMonthlyPSC",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "Total Monthly PSC",
                value: handleFormatPriceString(totalAgentPscThisMonth)
            }
        },
        {
            id: "TotalMonthlyDOC",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "Total Monthly DOC",
                value: handleFormatPriceString(totalAgentDocThisMonth)
            }
        },
        {
            id: "TotalMonthlyTOC",
            roles: ["Agent"],
            Component: DsNumberCard,
            props:
            {
                title: "Total MonthlyTOC",
                value: handleFormatPriceString(totalAgentTocThisMonth)
            }
        },
    ];

    const visibleItems = items.filter(item => {
        if (item.roles.includes("Agent") && isAgent) return true;
        if (item.roles.includes("Leader") && isLeader) return true;
        // Admin doesn't see these specific personal/group stats unless they also have the role
        return false;
    });

    return (
        <div className="container">
            {isAdmin && !isAgent && !isLeader && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4">Welcome, Admin</Typography>
                    <Typography variant="body1">Select a module from the menu to get started.</Typography>
                </Box>
            )}

            <Grid
                container
                spacing={4}
                alignItems="stretch"
                wrap="wrap"
                justifyContent="flex-start" /* left-align rows so last row isn't centered */
            >
                {isLoading ? (
                    // Loading Skeletons
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid item size={{ lg: 3, sm: 6, xs: 12 }} key={index}>
                            <Skeleton variant="rectangular" width={250} height={150} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))
                ) : (
                    visibleItems.map(({ id, Component, props }) => (
                        <Grid
                            item
                            size={{ lg: 3, sm: 6, xs: 12 }}
                            key={id}
                            sx={{ display: "flex" }} // makes item stretch vertically
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "stretch",
                                    justifyContent: "center",
                                    width: "250px",
                                    height: "150px",
                                    boxSizing: "border-box",
                                    p: 1,
                                    // ensure the rendered component fills the box
                                    "& > *": { width: "100%", height: "100%" },
                                }}
                            >
                                <Component {...props} />
                            </Box>
                        </Grid>
                    ))
                )}




                <Grid
                    container
                    spacing={4}
                    // alignItems="stretch"
                    wrap="wrap"
                    justifyContent="flex-start"
                // item
                // spacing={4}
                // sx={{ display: "flex" }} // makes item stretch vertically
                >
                    {/* Top Salesman Table (Leader Only) */}
                    {isAgent && (
                        <Grid
                            size={{
                                lg: 4,
                                md: 6,
                                xs: 12,
                            }}
                        >
                            <DsTable
                                title="Top Salesman"
                                columns={topSalesmanColumns}
                                data={topSalesmanData}
                            />
                        </Grid>
                    )}

                    {/* Top Sales Team Table (Leader Only) */}
                    {isAgent && (
                        <Grid
                            size={{
                                lg: 8,
                                md: 12,
                                xs: 12,
                            }}
                        >
                            <DsTable
                                title="Top Sales Team"
                                columns={topSalesTeamColumns}
                                data={topSalesTeamData}
                            />
                        </Grid>
                    )}
                </Grid>

                {/* Latest Sales Table */}
                <Grid
                    size={{
                        lg: 12,
                        md: 12,
                        xs: 12,
                    }}
                >
                    <DsTable
                        title="Latest 20 Sales"
                        columns={latestSalesColumns}
                        data={latestSalesData}
                        onViewAll={() => navigate('/sales')} // Ensure navigate is available or passed
                    />
                </Grid>
            </Grid>
        </div>
    );
}